import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { PowersService, Range } from 'ngx-core-services';

import {
  AppState,
  SystemsState,
  getSystemDefinitionDeviceIdsForTelemetries,
  getSystemDefinitionConfiguration,
  requestSystemAlerts,
  selectedSystemMetadata,
  setSystemAlertsError,
  updateSystemAlerts,
  } from '..';
import { processAndGroupAlerts } from '../../lib/alerts/alert.utils';
import { ProcessAlertsResult } from '../../lib/alerts/alert.model';

const ALERTS_LIST_DEBUG = false;
const TAG = '[AlertsList]';
const log = (...args: any[]) => { if (ALERTS_LIST_DEBUG) { console.log(...args); } };

@Injectable()
export class AlertsListEffects {

  requestSystemDailyAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemAlerts),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getSystemDailyAlerts(action.systemId, action.range, action.forceRefresh, action.showAll, systemState)).pipe(
          map((result) => updateSystemAlerts({
            systemId: action.systemId,
            instanceId: action.instanceId,
            alerts: result.alerts,
            allTimestampsBySerial: result.allTimestampsBySerial
          })),
          catchError(_ => of(setSystemAlertsError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system power state' })))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private readonly powersService: PowersService,
  ) {}

  async getSystemDailyAlerts(systemId: string, range: Range, forceRefresh: boolean, showAll: boolean, state: SystemsState): Promise<ProcessAlertsResult> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);

    const serials = getSystemDefinitionDeviceIdsForTelemetries(system, { onlyInstalled: false, onlyInvertersOfEmsMonitoring: true });

    log(`${TAG} systemId=${systemId}, gwType=${systemConfiguration.monitoringType}, range=${JSON.stringify(range)}, serials=${serials}, showAll=${showAll}`);

    const powers = await this.powersService.fetchDevicesPowerTelemetryByRange(serials, systemConfiguration.monitoringType, range.from, forceRefresh, `${systemConfiguration.tz}`);

    log(`${TAG} powers received`, Object.entries(powers).map(([s, records]) => `${s}: ${records.length} records`).join(', '));

    return processAndGroupAlerts(powers, { showAll });
  }
}
